import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

interface HolidayPayoutModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (amount: number) => void;
    maxAmount?: number;
    currency?: string;
    loading?: boolean;
}

export const HolidayPayoutModal = ({
    isOpen,
    onClose,
    onConfirm,
    maxAmount,
    currency = 'SEK',
    loading = false
}: HolidayPayoutModalProps) => {
    const { t } = useTranslation();
    const [payoutAmount, setPayoutAmount] = useState<string>('');
    const [error, setError] = useState<string>('');

    if (!isOpen) return null;

    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setPayoutAmount(value);
        setError('');

        // Validate amount
        const amount = parseFloat(value);
        if (value && maxAmount && (amount < 1000 || amount > maxAmount)) {
            if (amount < 1000) {
                setError(t('PLEASE_ENTER_AMOUNT_ABOVE_1000_SEK'));
            } else {
                setError(t('PAY_OUT_AMOUNT_SHOULD_BE_LESS_THAN_HOLIDAY_SAVINGS_AMOUNT'));
            }
        }
    };

    const handleConfirm = () => {
        const amount = parseFloat(payoutAmount);
        if (!payoutAmount || amount < 1000 || (maxAmount && amount > maxAmount)) {
            if (!payoutAmount) {
                setError(t('PLEASE_ENTER_PAYOUT_AMOUNT'));
            } else if (amount < 1000) {
                setError(t('PLEASE_ENTER_AMOUNT_ABOVE_1000_SEK'));
            } else {
                setError(t('PAY_OUT_AMOUNT_SHOULD_BE_LESS_THAN_HOLIDAY_SAVINGS_AMOUNT'));
            }
            return;
        }
        onConfirm(amount);
        setPayoutAmount('');
        onClose();
        setError('');
    };

    const handleCancel = () => {
        setPayoutAmount('');
        setError('');
        onClose();
    };

    const showNote = parseFloat(payoutAmount) <= 5000;

    return createPortal(
        <div className="holiday-payout-modal-overlay sn-payout-modal-overlay" onClick={handleCancel}>
            <div className="holiday-payout-modal sn-payout-modal" onClick={(e) => e.stopPropagation()}>
                <div className="holiday-payout-modal-header">
                    <h3 className="holiday-payout-modal-title">
                        {t('REQUEST_HOLIDAY_PAY_PAYOUT')}
                    </h3>
                    <button 
                        className="holiday-payout-modal-close"
                        onClick={handleCancel}
                        aria-label="Close modal"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>

                <div className="holiday-payout-modal-body">
                    <div className="holiday-payout-balance-info">
                        <div className="holiday-payout-balance-item">
                            <span className="holiday-payout-balance-label">{t('AVAILABLE_BALANCE')}</span>
                            <span className="holiday-payout-balance-value">{maxAmount?.toLocaleString()} {currency}</span>
                        </div>
                    </div>

                    <div className="holiday-payout-input-group">
                        <label className="holiday-payout-input-label">
                            {t('ENTER_PAYOUT_AMOUNT')}
                        </label>
                        <div className="holiday-payout-input-wrapper">
                            <input
                                type="number"
                                value={payoutAmount}
                                onChange={handleAmountChange}
                                placeholder={t('ENTER_AMOUNT')}
                                className={`holiday-payout-input ${error ? 'holiday-payout-input-error' : ''}`}
                                min="1000"
                                max={maxAmount}
                                step="1"
                                disabled={loading}
                            />
                            <span className="holiday-payout-currency">{currency}</span>
                        </div>
                        {error && (
                            <div className="holiday-payout-error">
                                {error}
                            </div>
                        )}
                    </div>

                    {showNote && payoutAmount && (
                        <div className="holiday-payout-note">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M8 1.33334C4.31809 1.33334 1.33337 4.31806 1.33337 8.00001C1.33337 11.682 4.31809 14.6667 8 14.6667C11.682 14.6667 14.6667 11.682 14.6667 8.00001C14.6667 4.31806 11.682 1.33334 8 1.33334Z" stroke="#F59E0B" strokeWidth="1.5"/>
                                <path d="M8 5.33334V8.66668" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round"/>
                                <path d="M8 10.6667H8.00667" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                            <span>
                                {t('NOTE_190_SEK_WILL_BE_DEDUCTED_FROM_EACH_VACATION_PAYOUT')}
                            </span>
                        </div>
                    )}
                </div>

                <div className="holiday-payout-modal-footer">
                    <div className="holiday-payout-modal-actions">
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="holiday-payout-btn-cancel"
                            disabled={loading}
                        >
                            {t('CANCEL')}
                        </button>
                        <button
                            type="button"
                            onClick={handleConfirm}
                            className="holiday-payout-btn-confirm"
                            disabled={loading || !payoutAmount || !!error}
                        >
                            {loading ? (
                                <>
                                    <div className="holiday-payout-spinner"></div>
                                    {t('PROCESSING')}
                                </>
                            ) : (
                                t('REQUEST_PAYOUT')
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};